import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, Flex, Layout, notification, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatNumberWithCommas, inputNumber } from 'src/app/shared/utils'
import { useNavigate, useParams } from 'react-router-dom'
import CustomInput from 'src/app/shared/components/common/input'
import { getTransactionDetailById } from 'src/app/shared/services/ViewTransactionService'
import ACHForm from './ACHForm'
import CheckForm from './CheckForm'
import './index.scss'
import {
  colorBgButtonSubtleHovered,
  colorBgErrorBold,
  colorBgErrorHovered,
  colorBgSuccessHovered,
  colorBgWarningHovered,
  colorBlack,
  colorBorderInfo,
  colorTextSubtle,
  success,
  warning,
} from 'src/app/themes/primaryThemeDefault'
import StatusTransaction from './StatusTransaction'
import { TRANSACTION_STATUS } from './index.constant'
import { getFullClientByIdWithoutPermission } from 'src/app/shared/services/ClientSetupService'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'

const ViewTransactionContent: React.FC = () => {
  const { t } = useTranslation()
  const params = useParams()
  const userProfile = useSelector(getUserProfile)
  const { id } = params
  const [isFetched, setIsFetched] = useState(false)
  const navigate = useNavigate()
  const [api, contextHolder] = notification.useNotification()
  const getTransactionInfo = async () => {
    if (id) {
      try {
        const res = await getTransactionDetailById(id)
        if (res?.data) {
          let clientName = ''
          if (userProfile?.role === USER_ROLE.CLIENT) {
            clientName = userProfile?.client?.clientName ?? ''
          } else {
            const clientInfo = await getFullClientByIdWithoutPermission({
              clientId: res?.data?.clientId,
            })

            clientName = clientInfo?.data?.clientName
          }
          const formattedData = {
            ...res?.data,
            clientName,
          }

          setIsFetched(true)
          return formattedData
        }
      } catch (error: any) {
        api.error({
          message: t('common:error'),
          description: `${error}`,
          placement: 'topRight',
        })
        setIsFetched(true)
      }
    }
  }
  const { control, trigger } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getTransactionInfo(),
  })

  const getConfig = useCallback(
    (status: string) => {
      switch (status) {
        case TRANSACTION_STATUS.EXCEPTION:
          return {
            color: warning,
            backgroundColor: colorBgWarningHovered,
            text: t(`AllTransactions:viewTransaction.status_list.exception`),
          }
        case TRANSACTION_STATUS.ISSUED:
          return {
            color: success,
            backgroundColor: colorBgSuccessHovered,
            text: t(`AllTransactions:viewTransaction.status_list.issued`),
          }
        case TRANSACTION_STATUS.PAID:
          return {
            color: colorBorderInfo,
            backgroundColor: '#EDF9FF',
            text: t(`AllTransactions:viewTransaction.status_list.paid`),
          }
        case TRANSACTION_STATUS.RETURNED:
          return {
            color: colorBgErrorBold,
            backgroundColor: colorBgErrorHovered,
            text: t(`AllTransactions:viewTransaction.status_list.returned`),
          }
        case TRANSACTION_STATUS.STOP_PAYMENT:
          return {
            color: '#FFFAE7',
            backgroundColor: '#F8C100',
            text: t(`AllTransactions:viewTransaction.status_list.stop`),
          }
        case TRANSACTION_STATUS.VOID:
          return {
            color: colorTextSubtle,
            backgroundColor: colorBgButtonSubtleHovered,
            text: t(`AllTransactions:viewTransaction.status_list.void`),
          }
        default:
          return {
            color: colorTextSubtle,
            backgroundColor: colorBgButtonSubtleHovered,
            text: t(`AllTransactions:viewTransaction.status_list.void`),
          }
      }
    },
    [t],
  )

  return (
    <Layout.Content style={{ padding: '24px 24px 0 24px' }} className='view-transaction-container'>
      {contextHolder}
      {isFetched ? (
        <form>
          <Flex
            style={{
              flexDirection: 'column',
              gap: 24,
            }}
          >
            <Flex className='transaction-general-info'>
              <Controller
                control={control}
                name={'clientName'}
                render={({ field, fieldState }) => (
                  <div className='transaction-client'>
                    <Typography.Text
                      style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '20px',
                        color: colorBlack,
                        margin: 0,
                        marginRight: '8px',
                        textAlign: 'center',
                        padding: '0 8px 0 0',
                      }}
                      className='label'
                    >
                      {`${t('AllTransactions:viewTransaction.clientName')}:`}
                    </Typography.Text>
                    <Typography.Text
                      style={{
                        display: 'block',
                        fontWeight: 600,
                        fontSize: '22px',
                        lineHeight: '30px',
                        color: colorBlack,
                      }}
                      className='content'
                    >
                      {!field.value ? null : field.value}
                    </Typography.Text>
                  </div>
                )}
              />
              <Controller
                control={control}
                name={'status'}
                render={({ field, fieldState }) => (
                  <div className='transaction-status'>
                    <Typography.Text
                      style={{
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '20px',
                        color: colorBlack,
                        margin: 0,
                        marginRight: '8px',
                        textAlign: 'center',
                        padding: '0 8px',
                      }}
                      className='label'
                    >
                      {`${t('AllTransactions:viewTransaction.status')}:`}
                    </Typography.Text>
                    <StatusTransaction configs={getConfig(!field.value ? null : field.value)} />
                  </div>
                )}
              />
            </Flex>
            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'accountName'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.account_name')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={async (event) => {
                      field.onBlur()
                      await trigger('accountName')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <Controller
                control={control}
                name={'accountNumber'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.account_number')}
                    value={field.value}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('accountNumber')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
            </Flex>
            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'amount'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.amount')}
                    value={field.value ? formatNumberWithCommas(field.value) : 0}
                    onChange={field.onChange}
                    onBlur={async (event) => {
                      field.onBlur()
                      await trigger('amount')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <Controller
                control={control}
                name={'transactionType'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.transaction_type')}
                    value={field.value}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('transactionType')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
            </Flex>
            <ACHForm control={control as any} />
            <CheckForm control={control as any} />

            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'inputDate'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.input_date')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={async (event) => {
                      field.onBlur()
                      await trigger('inputDate')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <Controller
                control={control}
                name={'paidDate'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.paid_date')}
                    value={field.value}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('paidDate')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
            </Flex>
            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'exceptionDate'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.exception_date')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={async (event) => {
                      field.onBlur()
                      await trigger('exceptionDate')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <Controller
                control={control}
                name={'exception'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.exception')}
                    value={field.value}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('exception')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
            </Flex>
            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'decision'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.decision')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={async (event) => {
                      field.onBlur()
                      await trigger('decision')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <Controller
                control={control}
                name={'reason'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.reason')}
                    value={field.value}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('reason')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
            </Flex>
            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'decisionedBy'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.decisioned_by')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={async (event) => {
                      field.onBlur()
                      await trigger('decisionedBy')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <Controller
                control={control}
                name={'transactionCode'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.transaction_code')}
                    value={field.value}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('transactionCode')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
            </Flex>

            <Flex
              style={{
                gap: 24,
              }}
            >
              <Controller
                control={control}
                name={'postedDate'}
                render={({ field, fieldState }) => (
                  <CustomInput
                    label={t('AllTransactions:viewTransaction.posted_date')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={async () => {
                      field.onBlur()
                      await trigger('postedDate')
                    }}
                    error={fieldState.error}
                    className='ant-input-view-only'
                    disabled
                  />
                )}
              />
              <div style={{ flex: 1 }} />
            </Flex>

            <Flex
              style={{
                justifyContent: 'flex-end',
                gap: 8,
                paddingBottom: 24,
              }}
              className='button-group'
            >
              <Button type='default' onClick={() => navigate(-1)} className='back-button'>
                {t('common:back_btn')}
              </Button>
            </Flex>
          </Flex>
        </form>
      ) : (
        <Skeleton />
      )}
    </Layout.Content>
  )
}

export default ViewTransactionContent
